import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Building, MapPin, Download, Loader } from 'lucide-react';

const trafficSources = [
  { value: 'Not Selected', label: 'Select' },
  { value: 'google', label: 'Google' },
  { value: 'recurring', label: 'We worked together before' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'referral', label: 'Referral' },
  { value: 'clutch', label: 'Clutch' },
  { value: 'behance', label: 'Behance' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Somewhere Else' }
];

const StepFour = ({ wizardData, updateWizardData, onPrev, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    updateWizardData({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!wizardData.clientName?.trim()) {
      newErrors.clientName = 'Name is required';
    }
    
    if (!wizardData.clientEmail?.trim()) {
      newErrors.clientEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wizardData.clientEmail)) {
      newErrors.clientEmail = 'Please enter a valid email';
    }
    
    if (!wizardData.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const payload = {
        client_name: wizardData.clientName,
        client_email: wizardData.clientEmail,
        client_phone: wizardData.clientPhone || '',
        company_name: wizardData.companyName,
        traffic_source: wizardData.trafficSource,
        selectedGoal: wizardData.selectedGoal,
        selectedVideoTypes: wizardData.selectedVideoTypes.join(','),
        script_needed: wizardData.scriptNeeded,
        target_audience: wizardData.targetAudience,
        deadline_date: wizardData.deadline,
        submission_date: new Date().toISOString()
      };

      const response = await fetch('https://hook.us1.make.com/qrj1a3zu33lrr1xpnrhut6rw4nwybwnx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSubmit();
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = wizardData.clientName?.trim() && 
                   wizardData.clientEmail?.trim() && 
                   wizardData.companyName?.trim() &&
                   !isSubmitting;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Almost Done!</h2>
        <p className="text-gray-400">Just a few details to get your personalized quote</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-8 space-y-6"
      >
        {/* Name */}
        <div>
          <label className="block text-white font-medium mb-2">
            Full Name *
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              name="client-name"
              value={wizardData.clientName}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              placeholder="Enter your full name"
              className={`w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-md border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${
                errors.clientName 
                  ? 'border-red-500 focus:ring-red-500/20' 
                  : 'border-white/10 focus:border-amber-500/50 focus:ring-amber-500/20'
              }`}
            />
          </div>
          {errors.clientName && (
            <p className="text-red-400 text-sm mt-1">{errors.clientName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-white font-medium mb-2">
            Work Email *
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="email"
              name="client-email"
              value={wizardData.clientEmail}
              onChange={(e) => handleInputChange('clientEmail', e.target.value)}
              placeholder="Enter your work email"
              className={`w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-md border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${
                errors.clientEmail 
                  ? 'border-red-500 focus:ring-red-500/20' 
                  : 'border-white/10 focus:border-amber-500/50 focus:ring-amber-500/20'
              }`}
            />
          </div>
          {errors.clientEmail && (
            <p className="text-red-400 text-sm mt-1">{errors.clientEmail}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-white font-medium mb-2">
            Phone
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="tel"
              name="Client-Phone"
              value={wizardData.clientPhone}
              onChange={(e) => handleInputChange('clientPhone', e.target.value)}
              placeholder="Enter your phone number"
              className="w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg text-white placeholder-gray-400 focus:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
            />
          </div>
        </div>

        {/* Company Name */}
        <div>
          <label className="block text-white font-medium mb-2">
            Company Name *
          </label>
          <div className="relative">
            <Building className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              name="company-name"
              value={wizardData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Enter your company name"
              className={`w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-md border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${
                errors.companyName 
                  ? 'border-red-500 focus:ring-red-500/20' 
                  : 'border-white/10 focus:border-amber-500/50 focus:ring-amber-500/20'
              }`}
            />
          </div>
          {errors.companyName && (
            <p className="text-red-400 text-sm mt-1">{errors.companyName}</p>
          )}
        </div>

        {/* Traffic Source */}
        <div>
          <label className="block text-white font-medium mb-2">
            How did you find us?
          </label>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={wizardData.trafficSource}
              onChange={(e) => handleInputChange('trafficSource', e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg text-white focus:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300 appearance-none [color-scheme:dark]"
            >
              {trafficSources.map(source => (
                <option key={source.value} value={source.value} className="bg-gray-800">
                  {source.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      <div className="flex justify-between">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onPrev}
          disabled={isSubmitting}
          className="px-8 py-3 rounded-lg font-semibold bg-white/10 text-white hover:bg-white/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Previous
        </motion.button>
        
        <motion.button
          whileHover={{ scale: canSubmit ? 1.05 : 1 }}
          whileTap={{ scale: canSubmit ? 0.95 : 1 }}
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 ${
            canSubmit
              ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-900/50'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Download Your Quote</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default StepFour;